import MapEmbed from '@/components/feature/MapEmbed';
import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FloatingCTA from '@/components/feature/FloatingCTA';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { getVenueBySlug, ALL_VENUES } from '../data';

export default function VenuePage() {
  const { slug } = useParams<{ slug: string }>();
  const venue = getVenueBySlug(slug || '');

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: aboutRef, isVisible: aboutVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: whyRef, isVisible: whyVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: stibRef, isVisible: stibVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    if (!venue) return;

    document.title = venue.seoTitle;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', venue.seoDescription);
    }

    const schemas = [
      {
        id: 'venue-localbusiness',
        data: {
          '@context': 'https://schema.org',
          '@type': 'EventVenue',
          name: venue.name,
          url: `https://${venue.website}`,
          description: venue.description,
          address: {
            '@type': 'PostalAddress',
            streetAddress: venue.address,
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
          containedInPlace: {
            '@type': 'City',
            name: 'São Paulo',
          },
          image: `${siteUrl}/locais/${venue.slug}`,
          event: venue.eventTypes.map((et) => ({
            '@type': 'Event',
            name: et,
          })),
        },
      },
      {
        id: 'venue-organization',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'STIB - Palestra Silenciosa São Paulo',
          alternateName: 'STIB',
          url: siteUrl,
          description: 'Especialistas em aluguel de fones sem fio para palestra silenciosa nos principais locais de eventos de São Paulo.',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
        },
      },
      {
        id: 'venue-service',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Service',
          '@id': `${siteUrl}/locais/${venue.slug}#service`,
          name: `Palestra Silenciosa no ${venue.name}`,
          description: `Aluguel de fones sem fio para palestra silenciosa no ${venue.name}. Tecnologia RF premium, suporte técnico presencial e capacidade para eventos de qualquer porte em ${venue.location}.`,
          provider: { '@id': `${siteUrl}/#organization` },
          areaServed: {
            '@type': 'Place',
            name: venue.name,
            address: {
              '@type': 'PostalAddress',
              streetAddress: venue.address,
              addressLocality: 'São Paulo',
              addressRegion: 'SP',
              addressCountry: 'BR',
            },
          },
          serviceType: 'Locação de Equipamento de Áudio Sem Fio para Eventos',
          url: `${siteUrl}/locais/${venue.slug}`,
          category: venue.eventTypes,
        },
      },
      {
        id: 'venue-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Locais de Eventos', item: `${siteUrl}/locais` },
            { '@type': 'ListItem', position: 3, name: `Palestra Silenciosa ${venue.name}`, item: `${siteUrl}/locais/${venue.slug}` },
          ],
        },
      },
      {
        id: 'venue-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: venue.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [venue, siteUrl, slug]);

  if (!venue) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-6">
              <i className="ri-error-warning-line text-gray-400 text-3xl"></i>
            </div>
            <h1 className="font-heading font-bold text-3xl text-secondary mb-4">Local não encontrado</h1>
            <p className="font-body text-gray-500 mb-8">O venue que você procura não existe em nossa base.</p>
            <Link
              to="/locais"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-full font-body font-semibold text-sm hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-white text-sm"></i>
              Ver todos os locais
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-[85vh] w-full flex items-center justify-center overflow-hidden mt-[72px]"
      >
        <div className="absolute inset-0">
          <img
            src={venue.heroImageUrl}
            alt={`Palestra Silenciosa ${venue.name} — Fones sem fio para eventos`}
            className={`w-full h-full object-cover object-top transition-all duration-1000 ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/75"></div>
        </div>

        {/* Breadcrumb */}
        <div className="absolute top-24 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb
              variant="light"
              items={[
                { label: 'Home', to: '/' },
                { label: 'Locais', to: '/locais' },
                { label: venue.name },
              ]}
            />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <div className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-primary text-sm"></i>
            </div>
            {venue.neighborhood} — {venue.location}
          </div>

          <header>
            <h1 className={`font-heading font-extrabold text-4xl md:text-6xl lg:text-7xl text-white mb-6 tracking-tight leading-tight lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              palestra silenciosa<br />{venue.name.split(' ').slice(0, 2).join(' ').toLowerCase()}
            </h1>
          </header>

          <p className={`font-body font-light text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            A <strong className="text-white">STIB</strong> é fornecedora de fones sem fio para palestra silenciosa no <strong className="text-white">{venue.name}</strong>. Tecnologia RF premium, suporte técnico presencial e capacidade para eventos de qualquer porte.
          </p>

          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <i className="ri-arrow-right-line text-white text-lg"></i>
            </a>
            <a
              href={`https://wa.me/5511917776227?text=Olá! Vi a página da STIB sobre palestra silenciosa no ${venue.name} e gostaria de solicitar um orçamento.`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* ── ABOUT THE VENUE ────────────────────────────────── */}
      <section ref={aboutRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-700 ${aboutVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Sobre o Local
              </span>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary lowercase mb-8 leading-tight">
                {venue.name.toLowerCase()}
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-6">
                {venue.description}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {venue.capacity && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="font-body text-xs text-gray-400 uppercase tracking-wider mb-1">Capacidade</p>
                    <p className="font-heading font-bold text-secondary text-lg">{venue.capacity}</p>
                  </div>
                )}
                {venue.area && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="font-body text-xs text-gray-400 uppercase tracking-wider mb-1">Área</p>
                    <p className="font-heading font-bold text-secondary text-lg">{venue.area}</p>
                  </div>
                )}
                {venue.parking && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="font-body text-xs text-gray-400 uppercase tracking-wider mb-1">Estacionamento</p>
                    <p className="font-heading font-bold text-secondary text-lg">{venue.parking}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="font-body text-xs text-gray-400 uppercase tracking-wider mb-1">Localização</p>
                  <p className="font-heading font-bold text-secondary text-lg">{venue.neighborhood}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="font-body text-xs text-gray-400 uppercase tracking-wider mb-3">Tipos de Evento</p>
                <div className="flex flex-wrap gap-2">
                  {venue.eventTypes.map((et, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-accent/10 text-accent-700 px-3 py-1.5 rounded-full font-body text-xs">
                      <i className="ri-check-line text-accent-500 text-xs"></i>
                      {et}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                    <i className="ri-map-pin-line text-primary text-lg"></i>
                  </div>
                  <div>
                    <p className="font-body text-xs text-gray-400 uppercase tracking-wider mb-1">Endereço</p>
                    <p className="font-body text-sm text-gray-600 leading-relaxed">{venue.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className={`grid grid-cols-1 gap-4 transition-all duration-700 delay-200 ${aboutVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              {venue.features.map((feature, i) => (
                <div key={i} className="flex items-start gap-4 bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary/10 flex-shrink-0">
                    <i className="ri-star-line text-primary text-lg"></i>
                  </div>
                  <p className="font-body text-sm text-gray-600 leading-relaxed pt-1">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY SILENT LECTURE WORKS ───────────────────────── */}
      <section ref={whyRef} className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Contexto Técnico
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase mb-6 transition-all duration-700 ${whyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              por que palestra silenciosa funciona no {venue.name.split(' ')[0].toLowerCase()}?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {venue.whySilentLectureWorks.map((reason, i) => (
              <div
                key={i}
                className={`flex items-start gap-5 bg-white rounded-2xl p-8 border border-gray-100 hover:border-primary/20 transition-all duration-500 hover:-translate-y-1 ${whyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary/10 flex-shrink-0">
                  <span className="font-heading font-bold text-primary text-xl">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div>
                  <p className="font-body text-base text-gray-600 leading-relaxed">{reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STIB ADVANTAGES AT THIS VENUE ───────────────────── */}
      <section ref={stibRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Vantagens STIB
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase mb-6 transition-all duration-700 ${stibVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              stib no {venue.name.split(' ')[0].toLowerCase()}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venue.stibAdvantages.map((adv, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-8 border border-primary/10 hover:border-primary/30 transition-all duration-500 hover:-translate-y-1 ${stibVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary/10 mb-5">
                  <i className="ri-check-double-line text-primary text-xl"></i>
                </div>
                <p className="font-body text-sm text-gray-600 leading-relaxed">{adv}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL ────────────────────────────────────── */}
      {venue.testimonial && (
        <section className="py-20 bg-secondary">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="flex items-center justify-center gap-1 mb-6">
              {[...Array(venue.testimonial.rating)].map((_, i) => (
                <i key={i} className="ri-star-fill text-yellow-400 text-2xl"></i>
              ))}
            </div>
            <blockquote className="font-body text-xl md:text-2xl text-white leading-relaxed mb-8 italic">
              "{venue.testimonial.text}"
            </blockquote>
            <div>
              <p className="font-heading font-bold text-white text-lg">{venue.testimonial.name}</p>
              <p className="font-body text-white/60 text-sm">{venue.testimonial.company}</p>
            </div>
          </div>
        </section>
      )}

      {/* ── MAP ─────────────────────────────────────────────── */}
      {venue.mapEmbedUrl && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Localização
              </span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary lowercase">
                onde fica o {venue.name.split(' ')[0].toLowerCase()}
              </h2>
            </div>
            <div className="rounded-3xl overflow-hidden border border-gray-100" style={{ height: '400px' }}>
              <MapEmbed src={venue.mapEmbedUrl} title={'Mapa de localização do ' + venue.name} fallbackQuery={venue.name} />
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section ref={faqRef} className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Perguntas Frequentes
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase mb-6 transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              dúvidas sobre palestra silenciosa no {venue.name.split(' ')[0].toLowerCase()}
            </h2>
          </div>

          <div className="space-y-6">
            {venue.faqs.map((faq, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 border border-gray-100 hover:border-primary/20 transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h4 className="font-heading font-bold text-xl text-secondary mb-4">
                  <a href={`#faq-${index}`} className="hover:underline">{faq.question}</a>
                </h4>
                <p className="font-body text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OTHER VENUES ───────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Outros Locais
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary lowercase mb-4">
              também atendemos nesses venues
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {ALL_VENUES.filter((v) => v.slug !== venue.slug).map((v) => (
              <Link
                key={v.slug}
                to={`/locais/${v.slug}`}
                className="group bg-gray-50 rounded-2xl p-5 border border-gray-100 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <p className="font-heading font-bold text-secondary group-hover:text-primary transition-colors duration-300 text-base mb-1">
                  {v.name}
                </p>
                <p className="font-body text-xs text-gray-400 group-hover:text-gray-500 transition-colors duration-300">
                  {v.neighborhood}
                </p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/locais"
              className="inline-flex items-center gap-2 text-primary font-body font-semibold hover:underline cursor-pointer"
            >
              Ver todos os locais de eventos
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-primary"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section ref={ctaRef} className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-secondary to-secondary/90"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className={`font-heading font-bold text-4xl md:text-5xl text-white lowercase mb-6 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            pronto para seu evento no {venue.name.split(' ')[0].toLowerCase()}?
          </h2>
          <p className={`font-body text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-100 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Solicite um orçamento sem compromisso para palestra silenciosa no <strong>{venue.name}</strong>. Equipe técnica, fones premium e suporte completo para seu evento.
          </p>
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <i className="ri-arrow-right-line text-white text-lg"></i>
            </a>
            <a
              href={`https://wa.me/5511917776227?text=Olá! Vi a página da STIB sobre palestra silenciosa no ${venue.name} e gostaria de solicitar um orçamento.`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}